﻿import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Cell,
  List,
  Placeholder,
  Section,
  Spinner,
} from '@telegram-apps/telegram-ui';
import { ImportUploader } from '../components/ImportUploader';
import { useImportJobs } from '../hooks/useImportJobs';
import { downloadTemplate } from '../lib/download';
import { ErrorState, LoadingState } from '../components/StateViews';
import type { ImportJob, ImportStatus } from '../types/import';

const STATUS_META: Record<ImportStatus, { emoji: string; label: string }> = {
  pending: { emoji: '⏳', label: 'Ожидает' },
  processing: { emoji: '⚙️', label: 'Обрабатывается' },
  completed: { emoji: '✅', label: 'Завершён' },
  failed: { emoji: '❌', label: 'Ошибка' },
  partial: { emoji: '⚠️', label: 'Частично' },
};

function relativeRu(iso: string): string {
  const d = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((d - now) / 1000);
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' });

  if (abs < 60) return rtf.format(Math.round(diffSec), 'second');
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour');
  return rtf.format(Math.round(diffSec / 86400), 'day');
}

function subtitle(job: ImportJob): string {
  const meta = STATUS_META[job.status] ?? { emoji: '•', label: job.status };
  return `${meta.emoji} ${meta.label} · ${job.processed}/${job.total} · ${relativeRu(job.createdAt)}`;
}

export function ImportPage() {
  const navigate = useNavigate();
  const q = useImportJobs();

  const jobs = useMemo(() => q.data?.items ?? [], [q.data]);

  if (q.isPending && !q.data) return <LoadingState />;
  if (q.error) {
    const err = q.error instanceof Error ? q.error : new Error(String(q.error));
    return <ErrorState error={err} onRetry={() => void q.refetch()} />;
  }

  return (
    <List>
      <Section header="Импорт товаров">
        <p style={{ margin: 0, opacity: 0.75 }}>
          Скачай шаблон, заполни товары, упакуй в ZIP вместе с фото и загрузи.
        </p>
      </Section>

      <Section header="Загрузка ZIP">
        <ImportUploader />
      </Section>

      <Section header="Шаблон">
        <Button mode="outline" size="m" stretched onClick={() => void downloadTemplate()}>
          Скачать шаблон Excel
        </Button>
      </Section>

      <Section header="История импортов">
        {q.isFetching && jobs.length > 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 8 }}>
            <Spinner size="s" />
          </div>
        )}

        {jobs.length === 0 ? (
          <Placeholder
            header="Пока нет импортов"
            description="Когда запустите импорт, здесь появится история задач."
          />
        ) : (
          jobs.map((job) => (
            <Cell
              key={job.id}
              subtitle={subtitle(job)}
              onClick={() => navigate(`/import/${job.id}`)}
            >
              {job.fileName || `Импорт ${job.id.slice(0, 8)}`}
            </Cell>
          ))
        )}
      </Section>
    </List>
  );
}
